/**
 * Attendance calculation engine: policies, shifts, punches → PR / LOP / LOP/PR / PR/LOP.
 * Reads UI config from localStorage (AttendancePolicyComponent) until backend APIs exist.
 */

import {
  getEffectiveTimingConfig,
  HALF_DAY_LOP_PR,
  missingPunchMarkToStatus,
  normalizePolicyRecord,
  normalizeShiftRecord,
  penaltyToDayStatus,
} from "./attendancePolicyConfig";
import {
  canConsumeEarlyGrace,
  canConsumeLateGrace,
  recordGraceUsage,
} from "./attendanceGraceLedger";
import { getShiftOverrideForDay } from "./attendanceShiftOverride";
import { getLocalDayKey } from "./attendancePunchStorage";

export const POLICIES_STORAGE_KEY = "hrms_frontend_attendance_policies_v1";
export const SHIFTS_STORAGE_KEY = "hrms_frontend_attendance_shifts_v1";

const ORG_APPLICABILITY_KEYS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "location",
];

const STRUCTURAL_STATUS_CODES = new Set([
  "WO",
  "PH",
  "HO",
  "CO",
  "CW",
  "OD",
  "WFH",
]);

export function loadAttendancePolicies() {
  try {
    const raw = localStorage.getItem(POLICIES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    return list.map(normalizePolicyRecord);
  } catch {
    return [];
  }
}

export function loadAttendanceShifts() {
  try {
    const raw = localStorage.getItem(SHIFTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    return list.map(normalizeShiftRecord);
  } catch {
    return [];
  }
}

export function parseIndividualIds(policy) {
  const s = policy?.individualEmployeeIds;
  if (Array.isArray(s)) return s.map(String).filter(Boolean);
  return String(s || "")
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeEmploymentTypes(policy) {
  const a = policy?.employmentTypes;
  if (!Array.isArray(a) || a.length === 0) return null;
  return [...new Set(a.map(String))];
}

function employeeOrgField(employee, key) {
  const map = {
    branch: employee?.branchId ?? employee?.branch ?? employee?.branch_id,
    department: employee?.departmentId ?? employee?.department ?? employee?.department_id,
    designation:
      employee?.designationId ?? employee?.designation ?? employee?.designation_id,
    grade: employee?.gradeId ?? employee?.grade ?? employee?.grade_id,
    category: employee?.categoryId ?? employee?.category ?? employee?.category_id,
    subDepartment:
      employee?.subDepartmentId ??
      employee?.subDepartment ??
      employee?.sub_department_id,
    location: employee?.locationId ?? employee?.location ?? employee?.workLocation,
    groupId: employee?.groupId ?? employee?.attendanceGroupId ?? employee?.policyGroupId,
  };
  const v = map[key];
  return v != null && v !== "" ? String(v) : "";
}

function employeeIdentityTokens(employee) {
  return [
    employee?.id,
    employee?.employeeId,
    employee?.code,
    employee?.empCode,
    employee?.employeeCode,
  ]
    .filter((x) => x != null && x !== "")
    .map(String);
}

export function isPolicyEffectiveOnDate(policy, date) {
  if (!policy?.effectiveDate) return true;
  const ed = new Date(policy.effectiveDate);
  if (isNaN(ed.getTime())) return true;
  const d = date instanceof Date ? date : new Date(date);
  return ed <= d;
}

export function employeeMatchesPolicy(employee, policy) {
  if (!employee || !policy || policy.status === "inactive") return false;

  const tokens = new Set(employeeIdentityTokens(employee));
  const individualIds = parseIndividualIds(policy);
  if (individualIds.length > 0) {
    return individualIds.some((id) => tokens.has(String(id)));
  }

  if (policy.allEmployees) {
    const emTypes = normalizeEmploymentTypes(policy);
    if (emTypes) {
      const et = String(employee.employmentType || employee.employment_type || "");
      return emTypes.includes(et);
    }
    return true;
  }

  let matchedOrgField = false;
  for (const k of ORG_APPLICABILITY_KEYS) {
    const pv = policy[k] ? String(policy[k]) : "";
    if (!pv) continue;
    matchedOrgField = true;
    const ev = employeeOrgField(employee, k);
    if (ev !== pv) return false;
  }

  const emTypes = normalizeEmploymentTypes(policy);
  if (emTypes) {
    const et = String(employee.employmentType || employee.employment_type || "");
    if (!emTypes.includes(et)) return false;
  }

  return matchedOrgField || !!emTypes;
}

function policySpecificityScore(policy) {
  const ids = parseIndividualIds(policy);
  if (ids.length > 0) return 1000 + ids.length;
  let score = 0;
  for (const k of ORG_APPLICABILITY_KEYS) {
    if (policy[k]) score += 10;
  }
  const em = normalizeEmploymentTypes(policy);
  if (em) score += 5;
  if (policy.allEmployees) score += 1;
  return score;
}

export function resolvePolicyForEmployee(employee, policies, asOfDate = new Date()) {
  const list = Array.isArray(policies) ? policies : [];
  const active = list.filter(
    (p) => p && p.status !== "inactive" && isPolicyEffectiveOnDate(p, asOfDate)
  );
  const matching = active.filter((p) => employeeMatchesPolicy(employee, p));
  if (matching.length === 0) {
    return active.find((p) => p.isDefault) || active[0] || null;
  }
  return [...matching].sort((a, b) => policySpecificityScore(b) - policySpecificityScore(a))[0];
}

export function resolveShiftForPolicy(policy, shifts, shiftNameHint = null) {
  const list = Array.isArray(shifts) ? shifts : [];
  if (shiftNameHint) {
    const byName = list.find(
      (s) =>
        String(s?.name || "").trim().toLowerCase() ===
        String(shiftNameHint).trim().toLowerCase()
    );
    if (byName) return byName;
  }
  const shiftId = policy?.shiftGroupId;
  if (shiftId) {
    const linked = list.find((s) => String(s.id) === String(shiftId));
    if (linked) return linked;
  }
  return (
    list.find((s) => s?.isDefault) ||
    list.find((s) => String(s?.name || "").trim().toLowerCase() === "general") ||
    list[0] ||
    null
  );
}

/** Resolve shift including per-day override set before check-in. */
export function resolveShiftForEmployeeDay(
  employee,
  policy,
  shifts,
  date = new Date(),
  shiftNameHint = null
) {
  const dayKey = getLocalDayKey(date instanceof Date ? date.getTime() : date);
  const override = getShiftOverrideForDay(employee?.id, dayKey);
  if (override?.shift) return override.shift;
  if (override?.shiftName) {
    const byOverride = resolveShiftForPolicy(policy, shifts, override.shiftName);
    if (byOverride) return byOverride;
  }
  return resolveShiftForPolicy(policy, shifts, shiftNameHint);
}

export function isFlexibleShift(shift) {
  if (!shift) return false;
  if (shift.shiftType === "flexi" || shift.flexiEnabled) return true;
  const name = String(shift.name || "").toLowerCase();
  const id = String(shift.id || "").toLowerCase();
  return name.includes("flex") || id.includes("flex");
}

export function shiftWindowMinutes(shift) {
  if (!shift) return { start: 9 * 60, end: 18 * 60, overnight: false };
  const start = parseTimeToMinutes(shift.startTime) ?? 9 * 60;
  let end = parseTimeToMinutes(shift.endTime) ?? 18 * 60;
  const overnight = !!(shift.crossDayShift) || end <= start;
  return { start, end, overnight };
}

function flexiLateMinutes(checkInMin, config) {
  const coreStart = parseTimeToMinutes(config.coreHoursStart);
  if (coreStart == null || checkInMin == null) return { late: false, lateMinutes: 0 };
  const grace = Number(config.graceCheckInMinutes ?? 15);
  const lateAfter = Number(config.considerLateAfterMinutes ?? 15);
  const allowedLatest = coreStart + grace + lateAfter;
  const lateMinutes = Math.max(0, checkInMin - (coreStart + grace));
  return {
    late: checkInMin > allowedLatest,
    lateMinutes,
  };
}

/**
 * Late / early vs shift + merged policy/shift grace; supports grace occurrence budget.
 */
export function evaluateTimingRules(
  policy,
  shift,
  checkIn,
  checkOut,
  { employeeId = null, date = new Date(), consumeGrace = true } = {}
) {
  const config = getEffectiveTimingConfig(policy, shift);
  const reasons = [];

  if (!policy || isFlexibleShift(shift)) {
    const inMin = parseTimeToMinutes(checkIn);
    if (isFlexibleShift(shift) && inMin != null) {
      const flexi = flexiLateMinutes(inMin, config);
      if (flexi.late) {
        return applyLatePenaltyDecision({
          config,
          employeeId,
          date,
          late: true,
          earlyExit: false,
          lateMinutes: flexi.lateMinutes,
          consumeGrace,
          reasons: ["flexi_late"],
        });
      }
    }
    return {
      late: false,
      earlyExit: false,
      lateMinutes: 0,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: false,
      shortLeaveSuggested: false,
      requiresShiftOverride: false,
      config,
      reasons,
    };
  }

  const { start, end, overnight } = shiftWindowMinutes(shift);
  const inMin = parseTimeToMinutes(checkIn);
  const outMin = parseTimeToMinutes(checkOut);

  const graceIn = config.graceCheckInMinutes;
  const graceOut = config.graceCheckOutMinutes;
  const lateAfter = config.considerLateAfterMinutes;
  const earlyBefore = config.considerEarlyBeforeMinutes;

  let late = false;
  let earlyExit = false;
  let lateMinutes = 0;

  if (inMin != null) {
    const shiftStartWithGrace = start + graceIn;
    lateMinutes = Math.max(0, inMin - shiftStartWithGrace);
    const allowedLatest = start + graceIn + lateAfter;
    late = inMin > allowedLatest;
  }

  if (outMin != null && !overnight) {
    const allowedEarliest = end - graceOut - earlyBefore;
    earlyExit = outMin < allowedEarliest;
  }

  if (late) {
    return applyLatePenaltyDecision({
      config,
      employeeId,
      date,
      late: true,
      earlyExit,
      lateMinutes,
      consumeGrace,
      reasons: ["late_after_shift_grace"],
      shift,
    });
  }

  if (earlyExit) {
    return applyEarlyPenaltyDecision({
      config,
      employeeId,
      date,
      earlyExit: true,
      consumeGrace,
      reasons: ["early_departure"],
    });
  }

  return {
    late: false,
    earlyExit: false,
    lateMinutes: 0,
    forceLop: false,
    penaltyStatus: null,
    graceConsumed: false,
    shortLeaveSuggested: false,
    requiresShiftOverride: false,
    config,
    reasons,
  };
}

function applyLatePenaltyDecision({
  config,
  employeeId,
  date,
  late,
  earlyExit,
  lateMinutes,
  consumeGrace,
  reasons,
  shift,
}) {
  const window = config.graceOccurrenceWindow || "monthly";
  const allowedLate = config.lateGraceAllowedOccurrences;

  if (
    config.graceRulesEnabled &&
    employeeId &&
    canConsumeLateGrace(employeeId, date, allowedLate, window)
  ) {
    if (consumeGrace) recordGraceUsage(employeeId, date, { late: true, windowType: window });
    return {
      late: true,
      earlyExit,
      lateMinutes,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: true,
      shortLeaveSuggested: lateMinutes <= config.shortLeaveMaxMinutes,
      requiresShiftOverride: false,
      config,
      reasons: [...reasons, "grace_occurrence_applied"],
    };
  }

  const shortLeaveSuggested =
    lateMinutes > 0 &&
    lateMinutes <= config.shortLeaveMaxMinutes &&
    config.regularizationAllowed;

  const requiresShiftOverride =
    config.allowShiftOverride &&
    lateMinutes > config.considerLateAfterMinutes;

  const penaltyStatus = penaltyToDayStatus(config.latePenaltyType, "late");
  const forceLop = penaltyStatus === "LOP";

  return {
    late: true,
    earlyExit,
    lateMinutes,
    forceLop,
    penaltyStatus,
    penaltyType: config.latePenaltyType,
    graceConsumed: false,
    shortLeaveSuggested,
    requiresShiftOverride,
    config,
    reasons: [
      ...reasons,
      forceLop ? "late_penalty" : "late_no_penalty",
      shortLeaveSuggested ? "short_leave_window" : null,
      requiresShiftOverride ? "shift_override_recommended" : null,
    ].filter(Boolean),
  };
}

function applyEarlyPenaltyDecision({
  config,
  employeeId,
  date,
  earlyExit,
  consumeGrace,
  reasons,
}) {
  const window = config.graceOccurrenceWindow || "monthly";
  const allowedEarly = config.earlyGraceAllowedOccurrences;

  if (
    config.graceRulesEnabled &&
    employeeId &&
    canConsumeEarlyGrace(employeeId, date, allowedEarly, window)
  ) {
    if (consumeGrace) recordGraceUsage(employeeId, date, { early: true, windowType: window });
    return {
      late: false,
      earlyExit: true,
      lateMinutes: 0,
      forceLop: false,
      penaltyStatus: null,
      graceConsumed: true,
      shortLeaveSuggested: false,
      requiresShiftOverride: false,
      config,
      reasons: [...reasons, "early_grace_occurrence_applied"],
    };
  }

  const penaltyStatus = penaltyToDayStatus(config.earlyDeparturePenaltyType, "early");
  return {
    late: false,
    earlyExit: true,
    lateMinutes: 0,
    forceLop: penaltyStatus === "LOP",
    penaltyStatus,
    penaltyType: config.earlyDeparturePenaltyType,
    graceConsumed: false,
    shortLeaveSuggested: false,
    requiresShiftOverride: false,
    config,
    reasons: [...reasons, "early_penalty"],
  };
}

export function parseTimeToMinutes(raw) {
  if (raw == null || raw === "" || raw === "--") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
  }
  const s = String(raw).trim();
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 1e11) {
    const d = new Date(asNum);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
  }
  const iso = s.match(/T(\d{2}):(\d{2})/);
  if (iso) return parseInt(iso[1], 10) * 60 + parseInt(iso[2], 10);

  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const ap = ampm[3].toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return h * 60 + m;
  }
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);

  const d = new Date(s);
  if (!isNaN(d.getTime()) && s.includes("-")) {
    return d.getHours() * 60 + d.getMinutes();
  }
  return null;
}

export function computeWorkedHours(checkIn, checkOut, policy = {}, shift = null, options = {}) {
  const { treatOpenCheckoutAsNow = false } = options;
  const config = getEffectiveTimingConfig(policy, shift);
  let inMin = parseTimeToMinutes(checkIn);
  let outMin = parseTimeToMinutes(checkOut);

  if (inMin == null) return 0;
  if (outMin == null) {
    if (!treatOpenCheckoutAsNow) return 0;
    const now = new Date();
    outMin = now.getHours() * 60 + now.getMinutes();
  }

  let diffMin = outMin - inMin;
  if (diffMin < 0) diffMin += 24 * 60;

  let breakMin = 0;
  const method = config.breakDeductionMethod || "fixed";
  if (method === "fixed") {
    breakMin =
      Number(config.breakFixedMinutes) ||
      Number(shift?.unpaidBreakMinutes) ||
      Number(shift?.totalBreakMinutes) ||
      Number(shift?.breakMinutes) ||
      0;
  } else if (method === "shift") {
    breakMin =
      Number(shift?.unpaidBreakMinutes) ||
      Number(shift?.totalBreakMinutes) ||
      Number(shift?.breakMinutes) ||
      0;
  }

  const worked = Math.max(0, diffMin - breakMin) / 60;
  const unit = Number(config.workingHoursRoundingUnit) || 0;
  if (unit > 0) {
    const dir = config.workingHoursRoundingDirection || "nearest";
    const step = unit / 60;
    if (dir === "up") return Math.ceil(worked / step) * step;
    if (dir === "down") return Math.floor(worked / step) * step;
    return Math.round(worked / step) * step;
  }
  return Math.round(worked * 100) / 100;
}

function normalizeStatusCode(status) {
  if (!status || typeof status !== "string") return "";
  const s = status.trim().toUpperCase();
  if (s === "LWP") return "LOP";
  return s.split("/")[0].trim();
}

export function isStructuralAttendanceStatus(status) {
  if (typeof status === "string" && status.includes("/")) return false;
  const code = normalizeStatusCode(status);
  if (!code || code === "-" || code === "PR" || code === "LOP") return false;
  if (STRUCTURAL_STATUS_CODES.has(code)) return true;
  if (code.length >= 2 && code !== "PR" && code !== "LOP") return true;
  return false;
}

function missingPunchStatus(policy, shift) {
  const config = getEffectiveTimingConfig(policy, shift);
  return missingPunchMarkToStatus(config.defaultMarkingForMissingPunch);
}

function hoursBasedStatus(workedHours, policy, shift) {
  const config = getEffectiveTimingConfig(policy, shift);
  const fullThreshold = Number(config.fullDayIfHoursAtLeast ?? 8);
  const halfThreshold = Number(config.halfDayMarkIfHoursBelow ?? 4);
  const absentBelow = Number(config.absentMarkIfHoursBelow ?? 2);

  if (config.autoAbsentMarkingEnabled !== false && workedHours < absentBelow) {
    return "LOP";
  }
  if (workedHours >= fullThreshold) return "PR";
  if (
    config.autoHalfDayMarkingEnabled !== false &&
    workedHours >= halfThreshold &&
    workedHours < fullThreshold
  ) {
    return HALF_DAY_LOP_PR;
  }
  return "LOP";
}

export function dayHasPunchData(checkIn, checkOut, worklogRow = null) {
  if (worklogRow?.inMs) return true;
  const hasIn = checkIn != null && checkIn !== "" && checkIn !== "--";
  const hasOut = checkOut != null && checkOut !== "" && checkOut !== "--";
  return hasIn || hasOut;
}

/**
 * Pre-check-in validation (navbar, biometric, API). Does not block by default; warns when late.
 */
export function evaluateCheckInPunch({
  employee,
  checkInMs = Date.now(),
  policies = null,
  shifts = null,
  shiftNameHint = null,
}) {
  const policyList = policies ?? loadAttendancePolicies();
  const shiftList = shifts ?? loadAttendanceShifts();
  const date = new Date(checkInMs);
  const checkInClock = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const empId = employee?.id ?? employee?.employeeId;

  const policy = resolvePolicyForEmployee(employee, policyList, date);
  const shift = resolveShiftForEmployeeDay(
    employee,
    policy,
    shiftList,
    date,
    shiftNameHint
  );

  const warnings = [];
  let allowed = true;

  if (policy && policy.checkInBiometric === false) {
    warnings.push("Biometric check-in is disabled for your attendance policy.");
  }

  const timing = evaluateTimingRules(policy, shift, checkInClock, null, {
    employeeId: empId,
    date,
    consumeGrace: false,
  });

  if (timing.graceConsumed) {
    warnings.push(
      `Late check-in within grace allowance (${timing.lateMinutes} min after shift start).`
    );
  } else if (timing.late) {
    if (timing.shortLeaveSuggested) {
      warnings.push(
        `Late by ${timing.lateMinutes} min — consider short leave / regularization.`
      );
    }
    if (timing.requiresShiftOverride) {
      warnings.push(
        "You are late beyond grace. Change today's shift (Attendance → Codes and devices / shift override) before check-in, or half-day LOP may apply."
      );
    }
    if (timing.penaltyType === "half_day") {
      warnings.push("Policy may mark this day as LOP/PR (half day) due to late arrival.");
    } else if (timing.penaltyType === "full_day") {
      warnings.push("Policy may mark this day as full-day LOP due to late arrival.");
    }
  }

  return {
    allowed,
    policy,
    shift,
    timing,
    warnings,
    checkInMs,
    requiresShiftOverride: timing.requiresShiftOverride,
    shortLeaveSuggested: timing.shortLeaveSuggested,
    projectedPenaltyStatus: timing.penaltyStatus,
  };
}

/**
 * Evaluate a single calendar day.
 */
export function evaluateDayAttendance({
  employee,
  date,
  storedStatus = "-",
  checkIn = null,
  checkOut = null,
  policies = null,
  shifts = null,
  shiftNameHint = null,
  isToday = false,
  openSession = false,
}) {
  const policyList = policies ?? loadAttendancePolicies();
  const shiftList = shifts ?? loadAttendanceShifts();
  const d = date instanceof Date ? date : new Date(date);
  const empId = employee?.id ?? employee?.employeeId;

  if (isStructuralAttendanceStatus(storedStatus)) {
    return {
      status: null,
      workedHours: 0,
      policyId: null,
      shiftName: shiftNameHint,
      skipped: "structural",
    };
  }

  const policy = resolvePolicyForEmployee(employee, policyList, d);
  const shift = resolveShiftForEmployeeDay(employee, policy, shiftList, d, shiftNameHint);

  const hasIn = checkIn != null && checkIn !== "" && checkIn !== "--";
  const hasOut = checkOut != null && checkOut !== "" && checkOut !== "--";

  if (!hasIn && !hasOut) {
    return {
      status: null,
      workedHours: 0,
      policyId: policy?.id ?? null,
      shiftName: shift?.name ?? shiftNameHint,
      skipped: "no_punch",
    };
  }

  if (!policy) {
    const worked = computeWorkedHours(checkIn, checkOut, {}, shift, {
      treatOpenCheckoutAsNow: false,
    });
    if (isToday && openSession) {
      return { status: "LOP", workedHours: worked, policyId: null, shiftName: shift?.name ?? null };
    }
    return {
      status: worked >= 8 ? "PR" : "LOP",
      workedHours: worked,
      policyId: null,
      shiftName: shift?.name ?? null,
    };
  }

  if (isToday && openSession) {
    return {
      status: "LOP",
      workedHours: computeWorkedHours(checkIn, checkOut, policy, shift, {
        treatOpenCheckoutAsNow: true,
      }),
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: ["open_session"],
    };
  }

  if (hasIn && !hasOut) {
    return {
      status: missingPunchStatus(policy, shift),
      workedHours: 0,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      skipped: "missing_checkout",
      reasons: ["missing_checkout"],
    };
  }

  const workedHours = computeWorkedHours(checkIn, checkOut, policy, shift, {
    treatOpenCheckoutAsNow: false,
  });

  const timing = evaluateTimingRules(policy, shift, checkIn, checkOut, {
    employeeId: empId,
    date: d,
    consumeGrace: true,
  });

  if (timing.penaltyStatus) {
    return {
      status: timing.penaltyStatus,
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: timing.reasons || ["timing_penalty"],
      timing,
    };
  }

  if (timing.forceLop) {
    return {
      status: "LOP",
      workedHours,
      policyId: policy.id,
      shiftName: shift?.name ?? null,
      reasons: timing.reasons || ["timing_violation"],
      timing,
    };
  }

  const status = hoursBasedStatus(workedHours, policy, shift);
  return {
    status,
    workedHours,
    policyId: policy.id,
    shiftName: shift?.name ?? null,
    reasons: [
      ...(timing.late ? ["late"] : []),
      ...(timing.earlyExit ? ["early_exit"] : []),
      ...(timing.graceConsumed ? ["grace_used"] : []),
      ...(timing.shortLeaveSuggested ? ["short_leave_window"] : []),
    ],
    timing,
  };
}

export function mergeDayDetailWithWorklog(dayDetail = {}, worklogRow = null) {
  if (!worklogRow) return dayDetail;
  const formatClock = (ms) =>
    new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const checkIn = formatClock(worklogRow.inMs);
  const checkOut =
    worklogRow.outMs != null ? formatClock(worklogRow.outMs) : dayDetail.checkOut ?? "--";
  let totalHours = dayDetail.totalHours ?? "--";
  if (worklogRow.outMs != null) {
    const diff = Math.max(0, worklogRow.outMs - worklogRow.inMs);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    totalHours = `${h}h ${String(m).padStart(2, "0")}m`;
  }
  return {
    ...dayDetail,
    checkIn,
    checkOut,
    totalHours,
    shiftAssigned: dayDetail.shiftAssigned || dayDetail.shiftName,
  };
}

export function buildMonthPolicyStatusMap({
  employee,
  year,
  month,
  statusMap = {},
  detailMap = {},
  worklogByDay = {},
  daysInMonth,
  now = new Date(),
  loggedInEmployeeId = null,
  autoApplyForAllEmployees = false,
}) {
  const policies = loadAttendancePolicies();
  const shifts = loadAttendanceShifts();
  const dim = daysInMonth ?? new Date(year, month + 1, 0).getDate();
  const result = {};
  const todayNum =
    now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const empId = String(employee?.id ?? "");
  const isSelf =
    loggedInEmployeeId != null && empId !== "" && String(loggedInEmployeeId) === empId;

  for (let day = 1; day <= dim; day++) {
    const date = new Date(year, month, day);
    const dayKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const cellNum = year * 10000 + (month + 1) * 100 + day;
    const stored = statusMap[String(day)] ?? statusMap[day] ?? "-";
    const detail = detailMap[String(day)] || detailMap[day] || {};
    const worklog = worklogByDay[dayKey] || null;
    const merged = mergeDayDetailWithWorklog(detail, worklog);
    const override = getShiftOverrideForDay(employee?.id, dayKey);
    if (override?.shiftName) {
      merged.shiftAssigned = override.shiftName;
    }

    if (cellNum > todayNum) {
      result[String(day)] = "-";
      continue;
    }

    if (isStructuralAttendanceStatus(stored)) {
      result[String(day)] = stored;
      continue;
    }

    const isToday =
      year === now.getFullYear() && month === now.getMonth() && day === now.getDate();
    const hasPunch = dayHasPunchData(merged.checkIn, merged.checkOut, worklog);
    const openSession = isToday && worklog?.inMs && (worklog.outMs == null || worklog.outMs === undefined);

    const shouldEvaluate =
      autoApplyForAllEmployees ||
      hasPunch ||
      (isSelf && (isToday || hasPunch));

    if (!shouldEvaluate) {
      result[String(day)] = stored && stored !== "-" ? stored : "-";
      continue;
    }

    if (isSelf && !hasPunch && !isToday) {
      result[String(day)] = stored && stored !== "-" ? stored : "-";
      continue;
    }

    if (isToday && isSelf && !hasPunch) {
      result[String(day)] = "-";
      continue;
    }

    const evalResult = evaluateDayAttendance({
      employee,
      date,
      storedStatus: stored,
      checkIn: merged.checkIn,
      checkOut: merged.checkOut,
      policies,
      shifts,
      shiftNameHint: merged.shiftAssigned || merged.shiftName,
      isToday,
      openSession,
    });

    if (evalResult.status) {
      result[String(day)] = evalResult.status;
    } else if (stored && stored !== "-") {
      result[String(day)] = stored;
    } else {
      result[String(day)] = "-";
    }
  }

  return result;
}

export function recalculateDayAttendance(input) {
  return evaluateDayAttendance(input);
}
